import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ProductKeys } from '@atlassiansox/asc-stargate';

import { ConfluenceTemplateMessages } from '@/components/confluence/templates/template-messages';
import { UseTemplateButton } from '@/components/confluence/templates/use-template-button';
import { ConfluenceTemplateAnalytics } from '@/components/confluence/templates/util/confluence-templates-analytics';
import { isAuthor } from '@/components/confluence/templates/util/template-util.ts';
import { useSiteSelectorContext } from '@/components/site-selector-modal/site-selector-context';
import { type Author, type Template } from '@/content/templates/template.types';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';

import { templateSvgLinks } from './template-svg-links';

export interface TemplateCardProps {
	template: Template;
	background?: string;
}

const fontCharlieText = `"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`;
const fontCharlieDisplay = `"Charlie Display", "Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`;

const templateCardContainerStyles = css({
	position: 'relative',
	borderRadius: '6px',
	width: '368px',
	minHeight: '400px',
	boxShadow: '0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 2px 0px rgba(9, 30, 66, 0.25)',
	overflow: 'hidden',

	boxSizing: 'border-box',
	transition: 'box-shadow 0.35s',
	'&:hover': {
		boxShadow: '0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 1px 1px 0px rgba(9, 30, 66, 0.25)',
		// Reason: to be fixed in https://hello.jira.atlassian.cloud/browse/GWP-2761
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.templateHoverStyles': {
			opacity: 0.8,
			background: wacToken('color.neutral.light100'),
		},
	},
	'@media (min-width: 48rem) and (max-width: 63.99rem)': {
		width: '320px',
	},
	'@media (max-width: 47.99rem)': {
		width: '320px',
	},
});

const templateCardInfoStyles = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	overflow: 'hidden',
	height: '100%',
	alignSelf: 'stretch',
});

const templateCardInfoWrapper = css({
	zIndex: 1,
	flex: 1,
	backgroundColor: wacToken('color.neutral.light0'),
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	padding: '24px 0px 0px 24px',
	borderRadius: '6px',
	cursor: 'pointer',
	height: '100%',
});

const templateCardTitleStyles = css({
	fontFamily: fontCharlieDisplay,
	fontSize: '20px',
	fontWeight: 700,
	lineHeight: '30px',
	letterSpacing: '0.3px',
	color: '#101214',
	margin: 0,
	'@media (max-width: 63.99rem)': {
		fontSize: '22px',
		lineHeight: '28px',
	},
});

const templateCardDescriptionStyles = css({
	fontFamily: fontCharlieText,
	fontSize: '16px',
	fontWeight: 400,
	lineHeight: '24px',
	color: '#253858',
	padding: '8px 24px 0px 0px',
	margin: '0px',
	'@media (min-width: 48rem)': {
		lineHeight: '21px',
	},
});

const templateCardActionStyles = css({
	position: 'absolute',
	insetBlockStart: '0',
	insetInlineStart: '0',
	width: '100%',
	height: '60%',
	borderRadius: '4px',
	backgroundColor: '#f8f8f8',
	transition: 'opacity 0.35s',
	opacity: '0',
	'@media (min-width: 48rem) and (max-width: 63.99rem)': {
		height: '55%',
	},
	'@media (max-width: 47.99rem)': {
		height: '50%',
	},
});

const templateCardActionOverlayStyles = css({
	height: '100%',
	position: 'relative',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'center',
	gap: '20px',
});

const templateCardLearnMoreStyles = css({
	color: wacToken('color.primary.blue'),
	border: 'none',
	pointerEvents: 'none',
	justifyContent: 'center',
	alignItems: 'center',
	fontFamily: fontCharlieText,
	fontSize: '14px',
	fontWeight: 600,
	lineHeight: '20px',
	gap: '4px',
});

const templatePreviewStyles = css({
	width: '100%',
	padding: '24px 24px 0px 24px',
});

const templateImageWrapper = css({
	flex: '0 200px',
	overflow: 'hidden',
	'@media (max-width: 47.99rem)': {
		flex: '0 200px',
	},
	backgroundSize: 'cover',
	backgroundRepeat: 'no-repeat',
	flexShrink: 0,
});

const templateCardLearnMoreLinkStyle = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	insetBlockEnd: 0,
	insetInlineEnd: 0,
});

const templateCardInfoHeaderAuthorStyles = css({
	fontFamily: fontCharlieText,
	fontSize: '14px',
	fontWeight: 400,
	lineHeight: '20px',
	padding: '16px 24px 16px 0px',
	color: wacToken('color.neutral.light700'),
});

export const TemplateCard = ({ template, background }: TemplateCardProps) => {
	const intl = useIntl();
	const learnMoreHref = useLocalizedHref(`/software/confluence/templates/${template.urlSlug}`);
	const { openModalForTemplate } = useSiteSelectorContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const author = isAuthor(template) ? (template.contributor as Author) : null;

	const companyName = isAuthor(template) ? author?.organization?.name : template.contributor?.name;

	const templateName = intl.formatMessage(ConfluenceTemplateMessages.templateTitle, {
		templateName: template.name ?? '',
	});

	const handleCardClick = () => {
		const templateId = template.pluginModuleKey ?? '';
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'linkButton',
			attributes: {
				templateName: templateName,
				label: templateName,
				path: location.pathname,
				event_type: 'ui',
				url: location.href,
				product: ProductKeys.CONFLUENCE,
				templateId,
				sandbox: true,
				trackEventName: 'wac.showroom.clicked-use-template',
			},
		}).fire();
		openModalForTemplate({
			templateId,
			templateName,
			productKey: ProductKeys.CONFLUENCE,
		});
	};

	const imgSrc =
		templateSvgLinks[template.urlSlug ?? ''] ??
		`https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/templates/taxonomy/${template.urlSlug}.svg`;

	const defaultImgSrc =
		'https://dam-cdn.atl.orangelogic.com/AssetLink/p17h52fiv34d06123v38e23cbi421x61.svg';

	const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (event.currentTarget.src !== defaultImgSrc) {
			event.currentTarget.src = defaultImgSrc;
		}
	};

	return (
		<UFOSegment name="confluence-templates-card">
			<div
				css={templateCardContainerStyles}
				data-cs-override-id={`template-card-container-${template.templateId}`}
			>
				<div
					css={templateCardInfoStyles}
					data-cs-override-id={`template-card-${template.templateId}`}
				>
					<div className="templateHoverStyles" css={[templateImageWrapper, css({ background })]}>
						{/* eslint-disable-next-line no-restricted-syntax */}
						<img css={templatePreviewStyles} src={imgSrc} alt="" onError={handleImageError} />
					</div>
					{/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
					{/* eslint-disable-next-line @atlassian/a11y/click-events-have-key-events,@atlassian/a11y/no-static-element-interactions,@atlassian/a11y/interactive-element-not-keyboard-focusable */}
					<div
						css={templateCardInfoWrapper}
						onClick={handleCardClick}
						data-testid="template-card-clickable"
					>
						<h2 css={templateCardTitleStyles}>{template.name}</h2>
						<p css={templateCardDescriptionStyles}>{template.previewBlurb}</p>
						<div>
							{companyName && (
								<div css={templateCardInfoHeaderAuthorStyles}>
									<FormattedMessage
										{...ConfluenceTemplateMessages.templateAuthorBy}
										values={{
											author: companyName,
										}}
									/>
								</div>
							)}
						</div>
					</div>
				</div>

				<div
					css={templateCardActionStyles}
					className="templateHoverStyles"
					data-cs-override-id={`template-card-actions-${template.templateId}`}
				>
					<div css={templateCardActionOverlayStyles}>
						{/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
						{/* eslint-disable-next-line @atlassian/a11y/anchor-has-content */}
						<a
							css={templateCardLearnMoreLinkStyle}
							href={learnMoreHref}
							{...ConfluenceTemplateAnalytics.learnMoreButton(template.urlSlug, 'template')}
						/>
						<UseTemplateButton
							templateName={templateName}
							blueprintModuleKey={template.pluginModuleKey}
						/>
						<span css={templateCardLearnMoreStyles}>
							<FormattedMessage {...ConfluenceTemplateMessages.cardLearnMore} />
						</span>
					</div>
				</div>
			</div>
		</UFOSegment>
	);
};
